import { action, makeObservable, observable } from 'mobx';

import {
  DEFAULT_ALLOWED_REPORT_CUT_SCORE_OBJ,
  DEFAULT_CUSTOM_REPORT_CUT_SCORE_OBJ,
  DEFAULT_MAX_REPORT_FACULTY_SCORE_CELLS_PER_PAGE,
  REPORT_ATTRIBUTE_FILTER_TYPE,
  REPORT_CONTEXT_TYPE
} from '../../services/reports/ReportConstants';

/**
 * **ReportOverrideManager** consists of Report observables with default values and respective action setters.
 *
 * The intention is to allow a given satellite to use ReportOverrideManager setters
 * to override ReportOverrideManager observables via the satellite's `index.js` file.
 */
export class ReportOverrideManager {
  constructor() {
    makeObservable(this);
  }

  @observable allowReportStandards = true;

  @observable allowReportCmapStandardsDropdown = false;

  @observable allowedReportCmapStandardsAttributeFilterTypes = [
    REPORT_ATTRIBUTE_FILTER_TYPE.ALL,
    REPORT_ATTRIBUTE_FILTER_TYPE.REPORTING_CATEGORY,
    REPORT_ATTRIBUTE_FILTER_TYPE.CLUSTERS,
    REPORT_ATTRIBUTE_FILTER_TYPE.READINESS_AND_SUPPORTING
  ];

  @observable allowReportCmapStandardsAttributeFiltersDropdown = false;

  @observable allowReportCmapStandardsMoreFiltersDropdown = false;

  @observable allowReportCmapStandardsGroupFilter = false;

  @observable customAllowedReportCutScores = DEFAULT_ALLOWED_REPORT_CUT_SCORE_OBJ;

  @observable customReportCutScores = DEFAULT_CUSTOM_REPORT_CUT_SCORE_OBJ;

  @observable allowCourseReportExpandableRowData = true;

  @observable allowLastModifiedAdditionalReportInfo = false;

  @observable allowLessonFilterDropdown = false;

  @observable allowProgressReportContext = false;

  @observable allowReportCustomCoursesTogglerWhenApplicable = true;

  @observable allowReportHideColumnsResetAllAsUnselectedAction = true;

  @observable allowReportHideColumnsResetAllToOriginalSelectionsAction = false;

  @observable allowReportHideRowsResetAllAsUnselectedAction = true;

  @observable allowReportHideRowsResetAllToOriginalSelectionsAction = false;

  @observable allowReportTableRowResourceExport = false;

  @observable allowUseCmapId = false;

  @observable allowUseDefaultImageSource = false;

  @observable reportContextTypeDefaultOrderArray = [
    REPORT_CONTEXT_TYPE.PERFORMANCE,
    REPORT_CONTEXT_TYPE.LIKERT,
    REPORT_CONTEXT_TYPE.USAGE,
    REPORT_CONTEXT_TYPE.PROGRESS,
    REPORT_CONTEXT_TYPE.ENGAGEMENT
  ];

  @observable reportTypeSelectorViewIconOverride = {
    courseIcon: null,
    standardsIcon: null
  };

  @observable shouldAppendCourseElementContentTypeAndElementDescriptionToReportTableRowTitle = false;

  @observable shouldExpandAllReportTableRowsByDefault = false;

  @observable shouldHideReportContentWithoutScoresByDefault = false;

  @observable shouldShowStandardsReportSummaryBar = false;

  @observable enableEnhancedUsageReports = false;

  @observable MAX_REPORT_FACULTY_SCORE_CELLS_PER_PAGE = DEFAULT_MAX_REPORT_FACULTY_SCORE_CELLS_PER_PAGE;

  @action clearAll = () => {
    /* placeholder */
  }

  @action setAllowReportStandards = (allowReportStandards) => {
    this.allowReportStandards = allowReportStandards;
  }

  @action setAllowReportCmapStandardsDropdown = (toggle) => {
    this.allowReportCmapStandardsDropdown = toggle;
  }

  @action setAllowedReportCmapStandardsAttributeFilterTypes = (allowedReportCmapStandardsAttributeFilterTypes = []) => {
    if (Array.isArray(allowedReportCmapStandardsAttributeFilterTypes)) {
      this.allowedReportCmapStandardsAttributeFilterTypes = allowedReportCmapStandardsAttributeFilterTypes;
    }
  }

  @action setAllowReportCmapStandardsAttributeFiltersDropdown = (toggle) => {
    this.allowReportCmapStandardsAttributeFiltersDropdown = toggle;
  }

  @action setAllowReportCmapStandardsMoreFiltersDropdown = (toggle) => {
    this.allowReportCmapStandardsMoreFiltersDropdown = toggle;
  }

  @action setAllowReportCmapStandardsGroupFilter = (toggle) => {
    this.allowReportCmapStandardsGroupFilter = toggle;
  }

  @action setCustomAllowedReportCutScores = (customAllowedReportCutScores) => {
    if (customAllowedReportCutScores && typeof customAllowedReportCutScores === 'object') {
      this.customAllowedReportCutScores = customAllowedReportCutScores;
    }
  }

  @action setCustomReportCutScores = (customReportCutScores) => {
    if (customReportCutScores && typeof customReportCutScores === 'object') {
      this.customReportCutScores = customReportCutScores;
    }
  }

  @action setAllowCourseReportExpandableRowData = (toggle) => {
    this.allowCourseReportExpandableRowData = toggle;
  }

  @action setAllowLastModifiedAdditionalReportInfo = (toggle) => {
    this.allowLastModifiedAdditionalReportInfo = toggle;
  }

  @action setAllowLessonFilterDropdown = (toggle) => {
    this.allowLessonFilterDropdown = toggle;
  }

  @action setAllowProgressReportContext = (toggle) => {
    this.allowProgressReportContext = toggle;
  }

  @action setAllowReportCustomCoursesTogglerWhenApplicable = (toggle) => {
    this.allowReportCustomCoursesTogglerWhenApplicable = toggle;
  }

  @action setAllowReportHideColumnsResetAllAsUnselectedAction = (toggle) => {
    this.allowReportHideColumnsResetAllAsUnselectedAction = toggle;
  }

  @action setAllowReportHideColumnsResetAllToOriginalSelectionsAction = (toggle) => {
    this.allowReportHideColumnsResetAllToOriginalSelectionsAction = toggle;
  }

  @action setAllowReportHideRowsResetAllAsUnselectedAction = (toggle) => {
    this.allowReportHideRowsResetAllAsUnselectedAction = toggle;
  }

  @action setAllowReportHideRowsResetAllToOriginalSelectionsAction = (toggle) => {
    this.allowReportHideRowsResetAllToOriginalSelectionsAction = toggle;
  }

  @action setAllowReportTableRowResourceExport = (toggle) => {
    this.allowReportTableRowResourceExport = toggle;
  }

  @action setAllowUseCmapId = (toggle) => {
    this.allowUseCmapId = toggle;
  }

  @action setAllowUseDefaultImageSource = (toggle) => {
    this.allowUseDefaultImageSource = toggle;
  }

  @action setReportContextTypeDefaultOrderArray = (reportContextTypeDefaultOrderArray) => {
    this.reportContextTypeDefaultOrderArray = reportContextTypeDefaultOrderArray;
  }

  @action setEnableEnhancedUsageReports = (enableEnhancedUsageReports) => {
    this.enableEnhancedUsageReports = enableEnhancedUsageReports;
  }

  @action setReportTypeSelectorViewIconOverride = ({ courseIcon = null, standardsIcon = null } = {}) => {
    this.reportTypeSelectorViewIconOverride = { courseIcon, standardsIcon };
  }

  @action setShouldAppendCourseElementContentTypeAndElementDescriptionToReportTableRowTitle = (toggle) => {
    this.shouldAppendCourseElementContentTypeAndElementDescriptionToReportTableRowTitle = toggle;
  }

  @action setShouldExpandAllReportTableRowsByDefault = (shouldExpandAllReportTableRowsByDefault) => {
    this.shouldExpandAllReportTableRowsByDefault = shouldExpandAllReportTableRowsByDefault;
  }

  @action setShouldHideReportContentWithoutScoresByDefault = (shouldHideReportContentWithoutScoresByDefault) => {
    this.shouldHideReportContentWithoutScoresByDefault = shouldHideReportContentWithoutScoresByDefault;
  }

  @action setShouldShowStandardsReportSummaryBar = (toggle) => {
    this.shouldShowStandardsReportSummaryBar = toggle;
  }

  @action setMaxReportFacultyScoreCellsPerPage = (maxReportFacultyScoreCellsPerPage) => {
    this.MAX_REPORT_FACULTY_SCORE_CELLS_PER_PAGE = maxReportFacultyScoreCellsPerPage;
  }
}

export default new ReportOverrideManager();
